import { ContextualLayer, ElementItem } from '@app/domains/layers/types/layers.types'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { contextualByIdAtom, elementByIdAtom, hazardByIdAtom } from '../state/data-submodule.atoms'
import { HazardScenario } from '@app/domains/hazards/types/hazard.types'

export type LayerType = 'Hazard' | 'Element' | 'Contextual'

export type ResolvedLayer =
  | { type: 'Hazard'; displayName?: string; tilesetIds: string[]; hazard: HazardScenario }
  | { type: 'Element'; displayName?: string; tilesetIds: string[]; element: ElementItem }
  | { type: 'Contextual'; displayName?: string; tilesetIds: string[]; contextual: ContextualLayer }
  | null

/**
 * Resolves a layer by its asset tag id, determining its type (Hazard, Element, or Contextual)
 * and returning the associated tileset ids, display name, and raw layer data.
 *
 * This hook is responsible for WHAT a layer is and HOW to render it on the map.
 *
 * @param id - The asset tag of the layer (e.g. "h164_l0_v3", "element_source", "contextual_id")
 * @returns A `ResolvedLayer` union discriminated by `type`, or `null` if not found.
 */
export const useResolvedLayer = (id?: string): ResolvedLayer => {
  const hazardAtom = useMemo(() => hazardByIdAtom(id ?? ''), [id])
  const elementAtom = useMemo(() => elementByIdAtom(id ?? ''), [id])
  const contextualAtom = useMemo(() => contextualByIdAtom(id ?? ''), [id])

  const hazard = useAtomValue(hazardAtom)
  const element = useAtomValue(elementAtom)
  const contextual = useAtomValue(contextualAtom)

  return useMemo<ResolvedLayer>(() => {
    if (hazard?.scenario) {
      return {
        type: 'Hazard',
        displayName: hazard.scenario.display_name,
        tilesetIds:
          hazard.scenario.tilesets?.map((t) => t.id).filter((id): id is string => !!id) ?? [],
        hazard: hazard.scenario,
      }
    }
    if (element?.element) {
      return {
        type: 'Element',
        displayName: element.element.display_name,
        tilesetIds:
          element.element.tilesets?.map((t) => t.id).filter((id): id is string => !!id) ?? [],
        element: element.element,
      }
    }
    if (contextual?.layer) {
      return {
        type: 'Contextual',
        displayName: contextual.layer.display_name,
        tilesetIds:
          contextual.layer.tilesets?.map((t) => t.id).filter((id): id is string => !!id) ?? [],
        contextual: contextual.layer,
      }
    }
    return null
  }, [hazard, element, contextual])
}
